import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { useEffect, useState } from "react";

import { useAtom } from "../../app-jotai";
import { openCloudDoc } from "../cloudActions";
import { cloudService } from "../cloudService";
import { versionHistoryDialogAtom } from "../dialogState";

import type { CloudVersion } from "../types";

export const VersionHistoryDialog = () => {
  const [state, setState] = useAtom(versionHistoryDialogAtom);
  const [versions, setVersions] = useState<CloudVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const docId = state.isOpen ? state.docId : null;

  useEffect(() => {
    if (!docId) {
      return;
    }
    setLoading(true);
    cloudService
      .listVersions(docId)
      .then(setVersions)
      .finally(() => setLoading(false));
  }, [docId]);

  if (!state.isOpen) {
    return null;
  }

  const close = () => setState({ isOpen: false });

  const restore = async (versionId: string) => {
    setBusyId(versionId);
    try {
      await cloudService.restoreVersion(state.docId, versionId);
      // Reload the doc's (new) current version into the canvas.
      await openCloudDoc(state.docId);
      close();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog
      size="wide"
      onCloseRequest={close}
      title={`Version history — ${state.name}`}
    >
      {loading ? (
        <div style={{ padding: "2rem", textAlign: "center" }}>Loading…</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "1rem",
            maxHeight: "60vh",
            overflowY: "auto",
            padding: "0.5rem",
          }}
        >
          {versions.map((v) => (
            <div
              key={v.versionId}
              style={{
                border:
                  v.type === "tagged"
                    ? "2px solid var(--color-primary)"
                    : "1px solid var(--color-gray-30)",
                borderRadius: "0.5rem",
                padding: "0.5rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.4rem",
              }}
            >
              {v.thumbnailUrl ? (
                <img
                  src={v.thumbnailUrl}
                  alt="version preview"
                  style={{
                    width: "100%",
                    height: 110,
                    objectFit: "contain",
                    background: "#fff",
                  }}
                />
              ) : (
                <div
                  style={{ height: 110, background: "var(--color-gray-10)" }}
                />
              )}
              <div style={{ fontSize: "0.78rem", fontWeight: 600 }}>
                {v.type === "tagged" ? "📌 " : "🕓 "}
                {v.description || (v.type === "auto" ? "Autosave" : "Version")}
              </div>
              <div
                style={{ fontSize: "0.7rem", color: "var(--color-gray-60)" }}
              >
                {new Date(v.createdAt * 1000).toLocaleString()}
              </div>
              <FilledButton
                size="medium"
                label={busyId === v.versionId ? "Restoring…" : "Restore"}
                onClick={() => restore(v.versionId)}
              />
            </div>
          ))}
          {!versions.length && (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "2rem",
              }}
            >
              No versions yet.
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
